import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    Output,
    Renderer2,
} from '@angular/core';
import { AumicoCoreProvider, NotificationType } from '@modeso/aumico-lib-core-fe';
import { I18NextService } from 'angular-i18next';

interface BulkEditAction {
    id: string;
    translationKey: string;
    maxSelections?: number;
    /**
     * REV-21798: render the action but block it. Used when the action is only
     * valid for a subset of the current selection — the user still sees that
     * the action exists and why it is unavailable, rather than the button
     * silently disappearing.
     */
    disabled?: boolean;
    /** Optional `data-testid` of the action button (e2e selector contracts). */
    testId?: string;
}

@Component({
    selector: 'bulk-edit-action-bar',
    template: `
        <div class="bulk-edit-action-bar">
            <ham-button
                class="close-button"
                variant="transparent"
                (click)="onActionClick('CLOSE_BULK_EDIT')"
            >X</ham-button>
            <div class="selected-count">{{ selectedCount }} {{ textSelectedTranslationKey | i18next }}</div>
            <div class="divider"></div>
            <ng-container *ngFor="let action of actions">
                <ham-button
                    variant="white"
                    outline="true"
                    [disabled]="!!action.disabled"
                    [attr.data-testid]="action.testId || null"
                    (click)="onActionClick(action)"
                >
                    {{ action.translationKey | i18next }}
                </ham-button>
            </ng-container>
            <div class="divider"></div>
            <ng-container *ngIf="deleteAction">
                <ham-button
                    [icon]="deleteActionIcon"
                    variant="red"
                    [disabled]="!!deleteAction.disabled"
                    (click)="onActionClick(deleteAction)"
                >
                    {{ deleteAction.translationKey | i18next }}
                </ham-button>
            </ng-container>
        </div>
    `,
    styleUrls: ['./bulk-edit-action-bar.component.scss'],
})
export class BulkEditActionBarComponent implements AfterViewInit, OnDestroy {
    @Input() actions: BulkEditAction[] = [];
    @Input() deleteAction: BulkEditAction | null = null;
    @Input() selectedCount: number = 0;
    @Input() textSelectedTranslationKey: string = ' Selected';
    @Input() globalPosition: boolean = true; // Controls whether to use global positioning
    @Input() deleteActionIcon: string = 'assets/hamIcons/trash-outline.svg';
    @Output() actionClicked = new EventEmitter<string>();

    @HostBinding('class.global-position') get isGlobalPosition(): boolean {
        return this.globalPosition;
    }

    private element: HTMLElement;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private i18next: I18NextService,
        private coreProvider: AumicoCoreProvider,
    ) { }

    ngAfterViewInit(): void {
        this.element = this.el.nativeElement;

        // Only move to global position if globalPosition is true
        if (this.globalPosition) {
            this.moveActionBarToGlobalPlaceholder();
        }
    }

    private moveActionBarToGlobalPlaceholder() {
        const placeholder = this.renderer.selectRootElement('#global-bottom-placeholder', true);
        if (placeholder) {
            // Use Renderer2 to append the action bar element to the global placeholder
            this.renderer.appendChild(placeholder, this.element);
        }
    }

    onActionClick(actionOrId: BulkEditAction | string) {
        if (typeof actionOrId !== 'string') {
            // REV-21798: ham-button's [disabled] suppresses the click in most
            // themes, but guard here too so a disabled action can never emit.
            if (actionOrId.disabled) {
                return;
            }
            if (actionOrId.maxSelections != null && this.selectedCount > actionOrId.maxSelections) {
                const description = this.i18next
                    .t('aumico_bulk_edit_action_bar_max_selection_error', { max: actionOrId.maxSelections })
                    .toString();
                this.coreProvider.dispatchAddNotification('', description, NotificationType.ERROR, 3500);
                return;
            }
        }

        const actionId = typeof actionOrId === 'string' ? actionOrId : actionOrId.id;
        this.actionClicked.emit(actionId);
    }

    ngOnDestroy() {
        // Clean up the DOM using Renderer2
        if (this.element && this.element.parentElement) {
            this.renderer.removeChild(this.element.parentElement, this.element);
        }
    }
}
